import React, { useMemo, useState } from "react";

import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { useListPropertiesQuery } from "../../../store/api/propertiesApi";

interface LeaseFiltersProps {
	onFilterChange: (filters: FilterState) => void;
	onSearch: (query: string) => void;
	onToggleView: (view: "table" | "timeline") => void;
	currentView: "table" | "timeline";
}

interface FilterState {
	status: string;
	expiration: string;
	property: string;
	rentRange: string;
}

const LeaseFilters: React.FC<LeaseFiltersProps> = ({
	onFilterChange,
	onSearch,
	onToggleView,
	currentView,
}) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [filters, setFilters] = useState<FilterState>({
		status: "",
		expiration: "",
		property: "",
		rentRange: "",
	});

	// Fetch properties from API
	const { data: propertiesData } = useListPropertiesQuery({ page_size: 1000 });

	const statusOptions = [
		{ value: "", label: "All Status" },
		{ value: "draft", label: "Draft" },
		{ value: "active", label: "Active" },
		{ value: "expired", label: "Expired" },
		{ value: "terminated", label: "Terminated" },
		{ value: "renewed", label: "Renewed" },
	];

	const expirationOptions = [
		{ value: "", label: "All Periods" },
		{ value: "30", label: "Expires in 30 days" },
		{ value: "60", label: "Expires in 60 days" },
		{ value: "90", label: "Expires in 90 days" },
		{ value: "expired", label: "Already Expired" },
	];

	// Build property options from API data
	const propertyOptions = useMemo(() => {
		const options = [{ value: "", label: "All Properties" }];
		if (propertiesData?.results) {
			propertiesData.results.forEach((property) => {
				options.push({
					value: property.id,
					label: `${property.name} - ${property.address}`,
				});
			});
		}
		return options;
	}, [propertiesData]);

	const rentRangeOptions = [
		{ value: "", label: "All Ranges" },
		{ value: "0-1000", label: "$0 - $1,000" },
		{ value: "1000-2000", label: "$1,000 - $2,000" },
		{ value: "2000-3000", label: "$2,000 - $3,000" },
		{ value: "3000+", label: "$3,000+" },
	];

	const handleFilterChange = (key: keyof FilterState, value: string) => {
		const newFilters = { ...filters, [key]: value };
		setFilters(newFilters);
		onFilterChange(newFilters);
	};

	const handleSearch = (value: string) => {
		setSearchTerm(value);
		onSearch(value);
	};

	const clearFilters = () => {
		const clearedFilters = {
			status: "",
			expiration: "",
			property: "",
			rentRange: "",
		};
		setFilters(clearedFilters);
		setSearchTerm("");
		onFilterChange(clearedFilters);
		onSearch("");
	};

	const hasActiveFilters =
		Object.values(filters).some((value) => value !== "") || searchTerm !== "";

	return (
		<div className="bg-card border border-border rounded-lg p-6 mb-6">
			{/* Search and View Toggle */}
			<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
				<div className="flex-1 max-w-md mb-4 lg:mb-0">
					<Input
						type="search"
						placeholder="Search leases by tenant, property, or lease ID..."
						value={searchTerm}
						onChange={(e) => handleSearch(e.target.value)}
						className="w-full"
					/>
				</div>

				<div className="flex items-center space-x-3">
					<div className="flex items-center bg-muted rounded-lg p-1">
						<Button
							variant={currentView === "table" ? "default" : "ghost"}
							size="sm"
							onClick={() => onToggleView("table")}
							iconName="Table"
							className="px-3"
						>
							Table
						</Button>
						<Button
							variant={currentView === "timeline" ? "default" : "ghost"}
							size="sm"
							onClick={() => onToggleView("timeline")}
							iconName="Calendar"
							className="px-3"
						>
							Timeline
						</Button>
					</div>
				</div>
			</div>

			{/* Filter Controls */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
				<Select
					label="Status"
					options={statusOptions}
					value={filters.status}
					onValueChange={(value) => handleFilterChange("status", String(value))}
					placeholder="Select status"
				/>

				<Select
					label="Expiration"
					options={expirationOptions}
					value={filters.expiration}
					onValueChange={(value) =>
						handleFilterChange("expiration", String(value))
					}
					placeholder="Select period"
				/>

				<Select
					label="Property"
					options={propertyOptions}
					value={filters.property}
					onValueChange={(value) =>
						handleFilterChange("property", String(value))
					}
					placeholder="Select property"
				/>

				<Select
					label="Rent Range"
					options={rentRangeOptions}
					value={filters.rentRange}
					onValueChange={(value) =>
						handleFilterChange("rentRange", String(value))
					}
					placeholder="Select range"
				/>
			</div>

			{/* Clear Filters */}
			{hasActiveFilters && (
				<div className="flex items-center justify-between pt-4 border-t border-border">
					<span className="text-sm text-muted-foreground">
						Active filters applied
					</span>
					<Button
						variant="ghost"
						size="sm"
						onClick={clearFilters}
						iconName="X"
						iconPosition="left"
					>
						Clear All Filters
					</Button>
				</div>
			)}
		</div>
	);
};

export default LeaseFilters;
